import React, { useCallback, useEffect, useRef } from 'react';
import stringify from '../../utils/stableStringify';

import AntQRCode from '../AntQRCode';
import AntAuth from '../../utils/AntAuth';
import AntString from '../../utils/AntString';
import cs from '../../utils/const';
import { AntGlobal } from '../../utils/AntContext';

const POLL_INTERVAL_MS = 3000;

function getBaseURL() {
    const storedURL = localStorage.getItem(cs.System_Code + '-Sys_BaseURL');
    if (!storedURL || storedURL === 'null' || storedURL === 'undefined') return cs.BaseURL;
    return storedURL;
}

async function ensureBaseURL() {
    const storedURL = localStorage.getItem(cs.System_Code + '-Sys_BaseURL');
    if (storedURL && storedURL !== 'null' && storedURL !== 'undefined') return storedURL;

    try {
        const response = await fetch(AntString.clean(cs.BaseURL + '/user/ACUserAuthen/getExternalSessionInfo'), {
            method: 'POST',
            headers: { Accept: 'application/json' }
        });
        const data = await response.json();
        let apiBaseURL = data?.data?.apiBaseURL;
        if (apiBaseURL) {
            if (!/^https?:\/\//i.test(apiBaseURL)) apiBaseURL = 'https://' + apiBaseURL;
            localStorage.setItem(cs.System_Code + '-Sys_BaseURL', apiBaseURL);
            return apiBaseURL;
        }
    } catch (error) {}

    return cs.BaseURL;
}

function getBrowserDeviceID() {
    const storageKey = cs.System_Code + '-qr-login-device-id';
    const storedID = localStorage.getItem(storageKey);
    if (storedID) return storedID;

    const generatedID = window.crypto && typeof window.crypto.randomUUID === 'function'
        ? window.crypto.randomUUID()
        : `web-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    localStorage.setItem(storageKey, generatedID);
    return generatedID;
}

export default function AccountLoginQRCode(props) {
    const appGlobal = AntGlobal();
    const checkResultTimer = useRef(null);
    const activeLoginQR = useRef(null);
    const pollInFlight = useRef(false);
    const remoteLoginInFlight = useRef(false);

    const postPublicJSON = useCallback(async (URL, body) => {
        const response = await fetch(AntString.clean(getBaseURL() + URL), {
            method: 'POST',
            body: AntString.clean(stringify(body)),
            headers: {
                'Content-Type': 'application/json',
                Accept: 'application/json'
            }
        });
        return response.json();
    }, []);

    const completeRemoteLogin = useCallback(async (data, qrMeta) => {
        const newSession = data?.data?.newSession;
        if (!newSession?.token || !qrMeta?.key || !qrMeta?.poll_token || remoteLoginInFlight.current) return false;
        remoteLoginInFlight.current = true;
        if (checkResultTimer.current) clearInterval(checkResultTimer.current);

        try {
            AntAuth.saveSessionData(newSession, false);
            try {
                await postPublicJSON('/qrcode/External/consume/ACUserAccountLogin', {
                    data: qrMeta.key,
                    poll_token: qrMeta.poll_token
                });
            } catch (error) {}

            if (props.onLogged) props.onLogged(data);
            window.location.reload();
            return true;
        } catch (error) {
            remoteLoginInFlight.current = false;
            return false;
        }
    }, [postPublicJSON, props.onLogged]);

    const checkLoginQRResult = useCallback(async (qrMeta) => {
        if (!qrMeta?.key || !qrMeta?.poll_token || pollInFlight.current || remoteLoginInFlight.current) return null;
        pollInFlight.current = true;
        try {
            const data = await postPublicJSON('/qrcode/External/getQRCodeData', {
                data: qrMeta.key,
                poll_token: qrMeta.poll_token
            });
            if (data?.data?.status === 'AccountLogged') await completeRemoteLogin(data, qrMeta);
            return data;
        } catch (error) {
            return null;
        } finally {
            pollInFlight.current = false;
        }
    }, [completeRemoteLogin, postPublicJSON]);

    const startPolling = useCallback((qrMeta) => {
        if (!qrMeta?.key || !qrMeta?.poll_token) return;
        activeLoginQR.current = qrMeta;
        if (checkResultTimer.current) clearInterval(checkResultTimer.current);
        checkResultTimer.current = setInterval(() => checkLoginQRResult(qrMeta), POLL_INTERVAL_MS);
    }, [checkLoginQRResult]);

    const generateLoginQRKey = useCallback(async (initURL, done, options = {}) => {
        try {
            if (options.heartbeat && activeLoginQR.current) {
                const currentResult = await checkLoginQRResult(activeLoginQR.current);
                if (currentResult?.data?.status === 'AccountLogged' || remoteLoginInFlight.current) return;
                if (currentResult?.data?.status === 'Verifying') {
                    if (done) done({
                        data: {
                            ...activeLoginQR.current,
                            ttl_seconds: 5,
                            expires_at: Math.floor(Date.now() / 1000) + 5
                        }
                    });
                    return;
                }
            }

            await ensureBaseURL();
            const data = await postPublicJSON(initURL, {
                app_id: appGlobal.appInfo.appId,
                app_version: appGlobal.appInfo.version || process.env.REACT_APP_VERSION,
                device_id: getBrowserDeviceID(),
                device_name: navigator.platform || navigator.userAgent || 'Web browser',
                device_type: 'Web',
                timezone: Intl.DateTimeFormat().resolvedOptions().timeZone
            });
            if (data?.error || !data?.data?.key || !data?.data?.poll_token) {
                if (done) done(data?.error ? data : { error: 'QR_INIT_FAILED' });
                return;
            }

            if (done) done(data);
            startPolling(data.data);
        } catch (error) {
            if (done) done({ error });
        }
    }, [appGlobal.appInfo.appId, appGlobal.appInfo.version, checkLoginQRResult, postPublicJSON, startPolling]);

    useEffect(() => () => {
        if (checkResultTimer.current) clearInterval(checkResultTimer.current);
    }, []);

    return (
        <div className="login-qr-code__card">
            <AntQRCode
                internalQR
                initURL={'/qrcode/External/init/ACUserAccountLogin'}
                displayURL={'/qrcode/External/displayLoginQRCode'}
                showTimer={false}
                refreshSeconds={60}
                refreshBeforeExpirySeconds={0}
                maxAutoRefreshes={10}
                pauseAutoRefreshOnError
                blockedSampleData={'sample'}
                resetOnPress
                refreshIconSize={24}
                refreshIconColor={'var(--AnteAppColor, #1D489A)'}
                imgStyle={{ width: 112, height: 112, borderRadius: 10, display: 'block' }}
                onCustomGenerateQRKey={generateLoginQRKey}
            />
        </div>
    );
}
